import React from 'react';
import { Formik, Form, FieldArray } from 'formik';
import * as Yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Chip,
  Grid,
  CircularProgress,
  Stack,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import { editEvaluators } from '../services/api';

const validationSchema = Yup.object().shape({
  question_pools: Yup.array().of(
    Yup.object().shape({
      id: Yup.string().required('Question pool ID is required'),
      evaluators: Yup.string().matches(
        /^(\s*\d+\s*,\s*)*(\s*\d+\s*)?$/,
        'Must be comma-separated numbers (e.g., 1, 2, 3)'
      ),
    })
  ),
  phase_evaluators: Yup.string().matches(
    /^(\s*\d+\s*,\s*)*(\s*\d+\s*)?$/,
    'Must be comma-separated numbers (e.g., 1, 2, 3)'
  ),
});

const EvaluationPhaseForm = ({ phase, group, phaseIndex, onSuccess, onError }) => {
  const mutation = useMutation({
    mutationFn: editEvaluators,
    onSuccess: () => {
      onSuccess('Evaluators updated successfully!');
    },
    onError: (error) => {
      onError(error?.response?.data?.message || 'Failed to update evaluators');
    },
  });

  const initialValues = {
    phase_evaluators: group?.evaluators
      ? group.evaluators.map(e => e.id).join(', ')
      : (phase.question_pools?.[0]?.evaluators?.join(', ') || ''),
    question_pools: phase.question_pools.map((pool) => ({
      id: pool.id,
      question_id: pool.question_id,
      evaluators: (pool.evaluators || []).join(', '),
    })),
  };

  const handleSubmit = (values) => {
    const isSubmissionWise = phase.evaluation_mode === 'SUBMISSION_WISE';
    const phaseEvaluatorsList = isSubmissionWise
      ? values.phase_evaluators.split(',').map(e => parseInt(e.trim(), 10)).filter(e => !isNaN(e))
      : [];

    const payload = {
      phase_id: phase.id,
      question_pools: values.question_pools.map((pool) => {
        let evaluatorsList = [];

        if (isSubmissionWise) {
          evaluatorsList = phaseEvaluatorsList;
        } else {
          evaluatorsList = pool.evaluators
            ? pool.evaluators
              .split(',')
              .map((e) => parseInt(e.trim(), 10))
              .filter((e) => !isNaN(e))
            : [];
        }

        return {
          id: pool.id,
          evaluators: evaluatorsList,
        };
      }),
    };
    mutation.mutate(payload);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        backgroundColor: 'transparent',
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
          <Chip
            label={`Phase ID: ${phase.id}`}
            size="small"
            variant="outlined"
          />
          <Chip
            label={phase.evaluation_mode.replace('_', ' ')}
            size="small"
            color="primary"
          />
          {phase.is_challenge_phase && (
            <Chip
              label="Challenge Phase"
              size="small"
              color="secondary"
            />
          )}
        </Stack>
      </Box>
      {(phase.evaluation_mode === 'SUBMISSION_WISE' || phase.evaluation_mode === 'QUESTION_WISE') ? (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, errors, touched, handleChange, handleBlur }) => (
            <Form>
              {phase.evaluation_mode === 'SUBMISSION_WISE' && (
                <Box sx={{ mb: 3, p: 2, backgroundColor: '#f0f7ff', borderRadius: 1, border: '1px solid #1976d2' }}>
                  <Typography variant="subtitle2" gutterBottom color="primary">
                    Phase Evaluators (Applies to ALL questions)
                  </Typography>
                  <TextField
                    fullWidth
                    label="Evaluators (Comma Separated)"
                    name="phase_evaluators"
                    value={values.phase_evaluators}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="e.g. 101, 102"
                    size="medium"
                    error={touched.phase_evaluators && Boolean(errors.phase_evaluators)}
                    helperText={touched.phase_evaluators && errors.phase_evaluators}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    * These evaluators will be assigned to every question below.
                  </Typography>
                </Box>
              )}
              <FieldArray name="question_pools">
                {({ push, remove }) => (
                  <Box>
                    {/* Only show question pools if NOT Submission Wise mode */}
                    {phase.evaluation_mode !== 'SUBMISSION_WISE' && values.question_pools.map((pool, poolIndex) => (
                      <Paper
                        key={poolIndex}
                        sx={{
                          p: 2,
                          mb: 2,
                          backgroundColor: '#fafafa',
                          border: '1px solid #eee',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                          }}
                        >
                          <Typography variant="subtitle2" color="primary">
                            Question Pool {poolIndex + 1}
                          </Typography>
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              disabled
                              label="Question ID"
                              name={`question_pools.${poolIndex}.question_id`}
                              value={pool.question_id}
                              size="small"
                              variant="outlined"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Evaluators (Comma Separated)"
                              name={`question_pools.${poolIndex}.evaluators`}
                              value={pool.evaluators}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="e.g. 101, 102, 103"
                              size="small"
                              error={
                                touched.question_pools?.[poolIndex]?.evaluators &&
                                Boolean(errors.question_pools?.[poolIndex]?.evaluators)
                              }
                              helperText={
                                touched.question_pools?.[poolIndex]?.evaluators &&
                                errors.question_pools?.[poolIndex]?.evaluators
                              }
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}
                  </Box>
                )}
              </FieldArray>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="small"
                  startIcon={
                    mutation.isPending ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SaveIcon fontSize="small" />
                    )
                  }
                  disabled={mutation.isPending}
                >
                  {mutation.isPending ? 'Saving...' : 'Save'}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      ) : null}
    </Paper>
  );
};

export default EvaluationPhaseForm;
